import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { motion } from 'framer-motion';
import { Bell, BookOpen, Cloud, Crown, LogOut, RotateCcw, Sparkles } from 'lucide-react';

import { Badge, Button, Card, Separator } from '@llura/ui';

import { CoachAvatar } from '../../shared/coach-avatar';
import { useCoachStore } from '../../stores/coach.store';
import { useProgressStore } from '../../stores/progress.store';
import { resetAllUserStores } from '../../stores/reset-all';
import { useStrings } from '../../hooks/useStrings';
import { signOut, useSession } from '../../lib/auth-client';

function useCountUp(target: number, durationMs = 800) {
  const [v, setV] = useState(0);
  useEffect(() => {
    const start = performance.now();
    let raf = 0;
    const tick = () => {
      const elapsed = performance.now() - start;
      const t = Math.min(1, elapsed / durationMs);
      // ease-out
      const eased = 1 - Math.pow(1 - t, 3);
      setV(Math.round(eased * target));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return v;
}

export function ProfilePage() {
  const navigate = useNavigate();
  const { strings } = useStrings();
  const { name, avatarUrl, tone } = useCoachStore();
  const { devotionLevel, sessionsCompleted } = useProgressStore();
  const session = useSession();

  const shownPoints = useCountUp(devotionLevel);

  const resetAll = () => {
    if (!confirm(strings.profile.resetConfirm)) return;
    resetAllUserStores();
    navigate('/');
  };

  const toneLabel = tone ? strings.coach.tones[tone].label : null;
  const sessionsLabel = sessionsCompleted === 1
    ? strings.progress.sessionsSingular
    : strings.progress.sessionsPlural;

  return (
    <div className="mx-auto w-full max-w-md space-y-5 px-4 py-5">
      <Card className="relative overflow-hidden border-border bg-card p-5">
        <div
          aria-hidden
          className="pointer-events-none absolute -top-10 right-[-20%] h-40 w-40 rounded-full bg-primary/15 blur-3xl"
        />
        <div className="flex items-center gap-4">
          <CoachAvatar name={name} src={avatarUrl} size={72} glow />
          <div className="min-w-0 flex-1">
            <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground">
              {strings.profile.yourCoachLabel}
            </div>
            <div className="font-serif text-2xl leading-tight">{name ?? '—'}</div>
            {toneLabel && (
              <Badge variant="outline" className="mt-1 text-muted-foreground">
                {toneLabel}
              </Badge>
            )}
          </div>
        </div>
      </Card>

      <Card className="relative overflow-hidden border-accent/40 bg-card p-6 text-center">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(at_top,hsl(var(--accent)/0.2),transparent_60%)]"
        />
        <div className="relative flex flex-col items-center gap-2">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
            <Crown className="h-3.5 w-3.5 text-accent" /> {strings.progress.unitName}
          </div>
          <motion.div
            key={shownPoints}
            initial={{ scale: 0.96 }}
            animate={{ scale: 1 }}
            className="font-serif text-6xl text-accent drop-shadow-[0_0_18px_hsl(var(--accent)/0.5)]"
          >
            {shownPoints}
          </motion.div>
          <div className="text-xs text-muted-foreground">
            {sessionsCompleted} {sessionsLabel}
          </div>
        </div>
      </Card>

      <Separator />

      <div className="space-y-2">
        <Button
          variant="outline"
          onClick={() => navigate('/profile/personality')}
          className="w-full justify-start gap-2 rounded-xl border-border"
        >
          <Sparkles className="h-4 w-4" />
          {strings.profile.yourPersonalityCta}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/profile/notifications')}
          className="w-full justify-start gap-2 rounded-xl border-border"
        >
          <Bell className="h-4 w-4" />
          {strings.profile.notificationsCta}
        </Button>
        <Button
          variant="outline"
          onClick={() => navigate('/journal')}
          className="w-full justify-start gap-2 rounded-xl border-border"
        >
          <BookOpen className="h-4 w-4" />
          {strings.profile.journalCta}
        </Button>
        <Button
          variant="outline"
          onClick={resetAll}
          className="w-full justify-start gap-2 rounded-xl border-destructive/30 text-destructive hover:bg-destructive/10 hover:text-destructive"
        >
          <RotateCcw className="h-4 w-4" />
          {strings.profile.resetCta}
        </Button>
      </div>

      <Separator />

      <section aria-labelledby="profile-sync-label" className="space-y-2">
        <h2
          id="profile-sync-label"
          className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground"
        >
          {strings.profile.sync.label}
        </h2>
        {session.isPending ? null : session.data ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">
              {strings.profile.sync.syncedAs.replace('{email}', session.data.user.email)}
            </p>
            <Button
              variant="outline"
              onClick={() => signOut().then(() => navigate('/'))}
              className="w-full justify-start gap-2 rounded-xl border-border"
            >
              <LogOut className="h-4 w-4" />
              {strings.profile.sync.signOutCta}
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{strings.profile.sync.signInDescription}</p>
            <Button
              variant="outline"
              onClick={() => navigate('/signup')}
              className="w-full justify-start gap-2 rounded-xl border-border"
            >
              <Cloud className="h-4 w-4" />
              {strings.profile.sync.signInCta}
            </Button>
          </div>
        )}
      </section>

      <p className="pt-2 text-center text-[10px] uppercase tracking-[0.3em] text-muted-foreground">
        {strings.profile.footer}
      </p>
    </div>
  );
}
